import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { toast } from "react-hot-toast";

// =================================================================
// MOCK DEPENDENCIES (for a self-contained, runnable example)
// =================================================================

// Mocking React Router and Auth hooks
const Link = ({ to, children, ...props }) => (
  <a href={to} {...props}>
    {children}
  </a>
);
const useAuth = () => {
  // This hook now simulates reading from localStorage as requested
  const [user, setUser] = useState(null);

  useEffect(() => {
    // In a real app, this would be your actual login logic.
    // For this example, we simulate a user object being in localStorage.
    const storedUser = JSON.parse(localStorage.getItem('user'))
    // In a real app, you would use: const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);

  return {
    user,
    hasPermission: (permission) => permission === "manage_users",
  };
};

// Mocking Layout component and react-icons

const FaEdit = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 576 512"
    fill="currentColor"
    className="icon"
  >
    <path d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.2 15.2-39.9 0-55.2z" />
  </svg>
);
const FaTrash = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    fill="currentColor"
    className="icon"
  >
    <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64s14.3 32 32 32h384c17.7 0 32-14.3 32-32s-14.3-32-32-32h-96l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32l21.2 339c1.6 25.3 22.6 45 47.9 45h245.8c25.3 0 46.3-19.7 47.9-45L416 128z" />
  </svg>
);
const FaChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 320 512"
    fill="currentColor"
    className="icon"
  >
    <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
  </svg>
);
const FaChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 320 512"
    fill="currentColor"
    className="icon"
  >
    <path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z" />
  </svg>
);

// =================================================================
// STYLES COMPONENT (replaces external CSS file)
// =================================================================
const UsersListStyles = () => (
  <style>{`
    .users-container { background-color: #f9fafb; min-height: 100vh; padding: 2rem; font-family: 'Inter', sans-serif; }
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
    .page-header h1 { font-size: 2rem; font-weight: 700; color: #1f2937; }
    .add-user-btn { background-color: #3b82f6; color: white; padding: 0.6rem 1rem; border-radius: 0.5rem; text-decoration: none; font-weight: 500; transition: background-color 0.2s; }
    .add-user-btn:hover { background-color: #2563eb; }
    .table-wrapper { background-color: white; border-radius: 0.75rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; }
    .users-table { width: 100%; border-collapse: collapse; }
    .users-table th, .users-table td { padding: 1rem 1.5rem; text-align: left; vertical-align: top; }
    .users-table th { background-color: #f3f4f6; color: #4b5563; font-size: 0.875rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb; }
    .users-table td { border-bottom: 1px solid #e5e7eb; }
    .users-table tbody tr:last-child td { border-bottom: none; }
    .contact-info .phone { font-size: 0.875rem; color: #6b7280; }
    .permissions-cell { display: flex; flex-wrap: wrap; gap: 0.5rem; max-width: 350px; }
    .permission-badge { background-color: #e5e7eb; color: #374151; padding: 0.25rem 0.5rem; border-radius: 9999px; font-size: 0.75rem; }
    .actions-cell { display: flex; gap: 1rem; }
    .action-btn { background: none; border: none; cursor: pointer; color: #6b7280; font-size: 1.1rem; }
    .action-btn:hover { color: #1f2937; }
    .icon { width: 1em; height: 1em; }
    .pagination { display: flex; justify-content: center; align-items: center; margin-top: 1.5rem; gap: 1rem; }
    .pagination button { background: white; border: 1px solid #d1d5db; padding: 0.5rem 1rem; border-radius: 0.5rem; display: flex; align-items: center; gap: 0.5rem; }
    .pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
    `}</style>
);

// =================================================================
// MAIN COMPONENT: Users
// =================================================================
const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, hasPermission } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);

  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!user?.token) {
        setError("Authentication required. Please log in.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/api/users`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data.users)
        ) {
          setUsers(response.data.data.users);
        } else {
          throw new Error("Invalid data format received from the server.");
        }
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || "Failed to fetch users."
        );
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      // Only fetch users if the user object (and token) is available
      fetchUsers();
    }
  }, [user, BASE_URL]);

  const handleDeleteClick = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${BASE_URL}/api/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setUsers(users.filter((u) => u._id !== userToDelete.id));
      toast.success(`User "${userToDelete.name}" deleted successfully.`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete user.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  // Pagination Logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users.length / usersPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  if (loading)
    return (
      <Layout>
        <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>
      </Layout>
    );
  if (error)
    return (
      <Layout>
        <div style={{ textAlign: "center", padding: "2rem", color: "red" }}>
          Error: {error}
        </div>
      </Layout>
    );

  return (
    <Layout>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete user "${userToDelete?.name}"? This action cannot be undone.`}
      />
      <UsersListStyles />
      <div className="users-container">
        <div className="page-header">
          <h1>User List</h1>
          {hasPermission("manage_users") && (
            <Link to="/add-user" className="add-user-btn">
              Add New User
            </Link>
          )}
        </div>

        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email & Phone</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length > 0 ? (
                currentUsers.map((userItem) => (
                  <tr key={userItem._id}>
                    <td>{userItem.name}</td>
                    <td>
                      <div className="contact-info">
                        <div>{userItem.email}</div>
                        <div className="phone">{userItem.phone}</div>
                      </div>
                    </td>
                    <td>
                      <div className="permissions-cell">
                        {userItem.permissions.map((permission) => (
                          <span key={permission} className="permission-badge">
                            {permission.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell">
                        {hasPermission("manage_users") && (
                          <>
                            <Link
                              to={`/users/edit/${userItem._id}`}
                              className="action-btn edit-btn"
                              title="Edit"
                            >
                              <FaEdit />
                            </Link>
                            <button
                              onClick={() => handleDeleteClick(userItem._id, userItem.name)}
                              className="action-btn delete-btn"
                              title="Delete"
                              disabled={isDeleting}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center" }}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaChevronLeft /> Prev
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next <FaChevronRight />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{title}</h3>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={onConfirm} className="btn btn-danger">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};
